import { useContext, useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';
import { PapiUser } from '@wix/da-papi-types';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { useTranslation } from 'react-i18next';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { Url } from '@wix/da-url';
import { getVideoCreatorRestrictions } from '../../../selectors/studio';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';

import s from './CoreBanner.scss';

export interface Props {
  currentUser?: PapiUser;
}

const CoreBanner: FC<Props> = ({ currentUser }) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const coreLevel = useSelector(getCoreLevel);

  const { videoPromptsRemaining } = useSelector(getVideoCreatorRestrictions);

  const nonCoreNoPrompts = useMemo(() => {
    if (!hasCoreMembership) {
      return videoPromptsRemaining < 1;
    } else {
      return false;
    }
  }, [hasCoreMembership, videoPromptsRemaining]);

  const isBasic = !hasCoreMembership || coreLevel === 'core_access';

  if (!isBasic) {
    return null;
  }

  return (
    <div className={s['root']}>
      {nonCoreNoPrompts ? (
        <div>
          <span className={s['no-prompts']}>
            {t('studio.videoCreator.button.noPrompts')}
          </span>

          <CoreSymbol className={s['core-icon']} size={IconSize.SMALL} />
          <div className={s['upsell']}>
            {t('studio.videoCreator.button.upsellNonCore')}
          </div>
          <BrandedButton
            size="small"
            variant="core-orange"
            className={s['core-upgrade-button']}
            href={Url.coreMembershipLink({ referer: Url.videoCreatorLink() })}
            target="_blank"
            biData={BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: ReferralType.VIDEO_CREATOR_BANNER_NO_PROMPTS,
            })}
          >
            {t('studio.videoCreator.coreUpsell.upgradeLink.nonCore')}
          </BrandedButton>
        </div>
      ) : (
        <div>
          <CoreSymbol className={s['core-icon']} size={IconSize.SMALL} />
          <div className={s['upsell']}>
            {t('studio.videoCreator.button.upsellCore')}
          </div>
          <BrandedButton
            size="small"
            variant="core-orange"
            className={s['core-upgrade-button']}
            href={Url.coreMembershipLink({ referer: Url.videoCreatorLink() })}
            target="_blank"
            biData={BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: ReferralType.VIDEO_CREATOR_BANNER,
            })}
          >
            {t('studio.videoCreator.button.upgradePlan')}
          </BrandedButton>
        </div>
      )}
    </div>
  );
};

CoreBanner.displayName = 'CoreBanner';
export default CoreBanner;
